import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { FormField } from '../../../../components/FormField'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { SearchableSelect } from '../../../../components/Select'
import { errorsOfField } from '../../../../helpers/tabErrorUtils'
import { IFormData } from './CoursesOnlineModal'

/** Limite de professores da página (validado também na API). */
export const TEACHERS_MAX = 6

/** Mesma regra da API: até {@link TEACHERS_MAX} professores na página. */
export const validateTeachers = (teacherIds: number[]): Record<string, string[]> =>
  teacherIds.length > TEACHERS_MAX ? { teacher_ids: [`O curso pode ter no máximo ${TEACHERS_MAX} professores.`] } : {}

interface IProps {
  formData: IFormData
  setFormData: React.Dispatch<React.SetStateAction<IFormData>>
  errors: Record<string, string[]>
  teachers: { id: number; name: string }[]
  isLoadingTeachers: boolean
  disabled?: boolean
}

/**
 * Professores da página do curso gravado (ABRA-437), na ordem do site, e a foto do tutor, que o site
 * só mostra quando o curso tem um professor.
 */
export function CourseOnlineTeachersTab({
  formData,
  setFormData,
  errors,
  teachers,
  isLoadingTeachers,
  disabled
}: IProps) {
  const isFull = formData.teacher_ids.length >= TEACHERS_MAX
  // O SearchableSelect mostra os escolhidos na ordem das opções, e não na do valor: os escolhidos vêm
  // primeiro, na ordem salva, para a tela (e o próximo onChange) manter a ordem do site.
  const options = useMemo(() => {
    const position = (id: number) => {
      const index = formData.teacher_ids.indexOf(id)
      return index === -1 ? teachers.length : index
    }
    return [...teachers].sort((a, b) => position(a.id) - position(b.id))
  }, [teachers, formData.teacher_ids])

  const nameOf = (id: number) => teachers.find((teacher) => teacher.id === id)?.name ?? `Professor #${id}`

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= formData.teacher_ids.length) return
    const next = [...formData.teacher_ids]
    ;[next[index], next[target]] = [next[target], next[index]]
    setFormData((prev) => ({ ...prev, teacher_ids: next }))
  }

  return (
    <Stack spacing={6}>
      <FormField
        label="Professores"
        error={errorsOfField(errors, 'teacher_ids')}
        helperText={`Até ${TEACHERS_MAX}, na ordem em que aparecem na página. Um professor desativado aparece no curso sem o link do perfil.`}
      >
        <SearchableSelect<number>
          isMulti
          options={options.map((teacher) => ({
            value: teacher.id,
            label: teacher.name,
            isDisabled: isFull && !formData.teacher_ids.includes(teacher.id)
          }))}
          value={formData.teacher_ids}
          onChange={(ids) => setFormData((prev) => ({ ...prev, teacher_ids: ids }))}
          placeholder="Selecione os professores"
          entity="professor"
          isLoading={isLoadingTeachers}
          isDisabled={disabled}
        />
      </FormField>

      {formData.teacher_ids.length > 1 && (
        <Box>
          <Text variant="caption" mb={2}>
            Ordem na página
          </Text>
          {formData.teacher_ids.map((teacherId, index) => (
            <Flex
              key={teacherId}
              align="center"
              justify="space-between"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              px={3}
              py={1}
              mt={index === 0 ? 0 : 2}
            >
              <Text fontSize="sm">
                {index + 1}. {nameOf(teacherId)}
              </Text>
              <Flex align="center" gap={1}>
                <ActionButton
                  action="move-up"
                  label={`Mover ${nameOf(teacherId)} para cima`}
                  mr={0}
                  onClick={() => move(index, -1)}
                  isDisabled={disabled || index === 0}
                />
                <ActionButton
                  action="move-down"
                  label={`Mover ${nameOf(teacherId)} para baixo`}
                  mr={0}
                  onClick={() => move(index, 1)}
                  isDisabled={disabled || index === formData.teacher_ids.length - 1}
                />
              </Flex>
            </Flex>
          ))}
        </Box>
      )}

      <Box maxW="320px">
        <ImageCropInput
          label="Foto do tutor"
          value={formData.tutor_image}
          onChange={(file) => setFormData((prev) => ({ ...prev, tutor_image: file, remove_tutor_image: false }))}
          maxSizeMB={5}
          previewHeight="240px"
          isDisabled={disabled}
          error={errors.tutor_image}
        />
        <Text variant="caption" mt={1}>
          Aparece só quando o curso tem um único professor.
        </Text>
        {formData.tutor_image && !disabled && (
          <ActionButton
            action="delete"
            label="Remover foto"
            showLabel
            variant="ghost"
            mt={2}
            onClick={() => setFormData((prev) => ({ ...prev, tutor_image: null, remove_tutor_image: true }))}
          />
        )}
      </Box>
    </Stack>
  )
}
